import { CUSTOM_FIELDS, FONTS, RTE_BUTTON_STATE } from 'appConstants';
import { isValidAnchor } from 'helpers';

const richTextEditorConfig = {
  actions: ['bold'],
};

// Curated pixel sizes for the RTE toolbar (a preset dropdown, not a free number box). Beginners get
// sensible choices and we avoid the fragile execCommand('fontSize',1-7) + <font size=7> diffing that
// misfired next to XXX-Large text.
const FONT_SIZE_PX_PRESETS = [12, 14, 16, 18, 20, 24, 28, 32, 36, 48];
// Line-height presets (applied to the selected component's style, so they export as a CSS rule).
const LINE_HEIGHT_PRESETS = [1, 1.15, 1.3, 1.5, 1.8, 2];

// Apply an exact pixel font-size to the current selection by wrapping it in a <span style>. Using
// the Range API directly (not execCommand) sidesteps the 1-7 legacy-size limit and the unreliable
// <font size=7> node diff. On a collapsed cursor (nothing selected) we size the WHOLE editable text
// instead of silently doing nothing — the beginner expectation for a size control.
const applyFontSizePx = (rte, px) => {
  const doc = rte.doc;
  if (!doc) return;
  const sel = doc.getSelection ? doc.getSelection() : null;
  if (!sel || !sel.rangeCount) return;
  let range = sel.getRangeAt(0);
  if (sel.isCollapsed) {
    if (!rte.el) return;
    range = doc.createRange();
    range.selectNodeContents(rte.el);
  }
  const span = doc.createElement('span');
  span.style.fontSize = `${px}px`;
  try {
    range.surroundContents(span);
  } catch (e) {
    // surroundContents throws on partial/multi-node boundaries → extract + wrap.
    span.appendChild(range.extractContents());
    range.insertNode(span);
  }
  // Keep the newly-sized text selected so the toolbar stays in context.
  const after = doc.createRange();
  after.selectNodeContents(span);
  sel.removeAllRanges();
  sel.addRange(after);
};

// Walk up from a node to the nearest ancestor <a>, stopping at the editable host.
const closestAnchor = (node, root) => {
  let el = node && node.nodeType === 3 ? node.parentElement : node;
  while (el && el !== root) {
    if (el.tagName === 'A') return el;
    el = el.parentElement;
  }
  return null;
};

// Text hyperlink editor. The built-in link action only opened a URL dialog for BUTTON components;
// selecting text and clicking link inserted an <a href=""> with no way to set the URL. This opens a
// small modal (reusing the edit-url-modal styles) to add / edit / remove a link on the current text
// selection. The DOM Range is captured BEFORE the modal opens, because focusing the modal clears the
// contenteditable selection; on save we refocus the editable and restore the range before inserting.
const openTextLinkModal = (editor, rte) => {
  const doc = rte.doc;
  const sel = doc && doc.getSelection ? doc.getSelection() : null;
  if (!sel || !sel.rangeCount) return;
  const savedRange = sel.getRangeAt(0).cloneRange();
  const existing = closestAnchor(sel.anchorNode, rte.el);
  const currentHref = existing ? existing.getAttribute('href') || '' : '';
  const currentTarget = existing
    ? existing.getAttribute('target') || '_self'
    : '_self';

  const restore = () => {
    if (rte.el && rte.el.focus) rte.el.focus();
    sel.removeAllRanges();
    sel.addRange(savedRange);
  };

  const form = document.createElement('div');
  form.className = 'edit-url-modal__form';

  const title = document.createElement('p');
  title.className = 'edit-url-modal__form-title';
  title.innerHTML = 'Add, edit or remove link';

  const urlLabel = document.createElement('label');
  urlLabel.className = 'dnd-label';
  urlLabel.setAttribute('for', 'text-link-url');
  urlLabel.innerHTML = 'Link';
  const urlInput = document.createElement('input');
  urlInput.type = 'url';
  urlInput.id = 'text-link-url';
  urlInput.placeholder = 'eg. https://google.com';
  urlInput.className = 'edit-url-modal__url-input dnd-input__input';
  urlInput.value = currentHref;

  const targetLabel = document.createElement('label');
  targetLabel.className = 'dnd-label';
  targetLabel.setAttribute('for', 'text-link-target');
  targetLabel.innerHTML = 'Target';
  const selectWrapper = document.createElement('div');
  selectWrapper.className = 'edit-url-modal__select-wrapper';
  const targetSelect = document.createElement('select');
  targetSelect.id = 'text-link-target';
  targetSelect.className = 'edit-url-modal__url-target';
  [
    ['_self', 'This window'],
    ['_blank', 'New window'],
  ].forEach(([value, text]) => {
    const option = document.createElement('option');
    option.value = value;
    option.text = text;
    if (value === currentTarget) option.setAttribute('selected', true);
    targetSelect.appendChild(option);
  });
  selectWrapper.appendChild(targetSelect);

  const buttons = document.createElement('div');
  buttons.className = 'edit-url-modal__buttons';

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'dnd-button-base dnd-button text default';
  cancelButton.innerHTML = '<div class="dnd-button__label">Cancel</div>';
  cancelButton.onclick = () => editor.Modal.close();

  const saveButton = document.createElement('button');
  saveButton.type = 'button';
  saveButton.className = 'dnd-button-base dnd-button contained default';
  saveButton.innerHTML = '<div class="dnd-button__label">Save</div>';
  saveButton.onclick = () => {
    const href = urlInput.value.trim();
    const target = targetSelect.value;
    if (existing) {
      if (href) {
        existing.setAttribute('href', href);
        existing.setAttribute('target', target);
        if (rte.el && rte.el.focus) rte.el.focus();
      } else {
        restore();
        rte.exec('unlink'); // clearing the URL removes the link
      }
    } else if (href) {
      restore();
      const label = savedRange.toString() || href;
      rte.insertHTML(`<a href="${href}" target="${target}">${label}</a>`);
    }
    editor.Modal.close();
  };

  // A "Remove" action is only meaningful when editing an existing link.
  if (existing) {
    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'dnd-button-base dnd-button text default';
    removeButton.innerHTML = '<div class="dnd-button__label">Remove</div>';
    removeButton.onclick = () => {
      restore();
      rte.exec('unlink');
      editor.Modal.close();
    };
    buttons.appendChild(removeButton);
  }
  buttons.appendChild(cancelButton);
  buttons.appendChild(saveButton);

  form.appendChild(title);
  form.appendChild(urlLabel);
  form.appendChild(urlInput);
  form.appendChild(targetLabel);
  form.appendChild(selectWrapper);
  form.appendChild(buttons);

  editor.Modal.setTitle('Link Settings')
    .setContent(form)
    .open({ attributes: { class: 'edit-url-modal' } });
};

const richTextEditor = (editor) => {
  const rte = editor.RichTextEditor;

  rte.remove('bold');

  rte.add('fontName', {
    icon: `<select class="gjs-rte-select">
      <option value="">Font family</option>
      ${FONTS.reduce((acc, font) => {
        acc += `<option value='${font.value}'>${font.name}</option>`;

        return acc;
      }, '')}
    </select>`,
    attributes: { title: 'Font family' },
    event: 'change',
    result: (rte, action) => {
      const { value } = action.btn.firstChild;

      rte.exec('fontName', value);

      const font = FONTS.find((font) => font.value === value);

      if (font?.url) {
        const onAddFont = editor.getModel().get('onAddFont');

        onAddFont(font);
        editor.runCommand('add-font', { url: font.url });
      }
    },
    update: (rte, action) => {
      const value = rte.doc.queryCommandValue(action.name);

      const font = FONTS.find(
        (font) =>
          font.value === value || font.value === value.replaceAll('"', ''),
      );

      if (font) {
        action.btn.firstChild.value = font.value;
        return;
      }

      action.btn.firstChild.value = '';
    },
  });

  rte.add('fontSize', {
    icon: `<select class="gjs-rte-select">
      <option value="1">X-Small</option>
      <option value="2">Small</option>
      <option value="3">Medium</option>
      <option value="4">Large</option>
      <option value="5">X-Large</option>
      <option value="6">XX-Large</option>
      <option value="7">XXX-Large</option>
    </select>`,
    event: 'change',
    attributes: { title: 'Font size' },
    result: (rte, action) => {
      const { value } = action.btn.firstChild;

      rte.exec('fontSize', value);
    },
    update: (rte, action) => {
      const value = rte.doc.queryCommandValue(action.name);

      action.btn.firstChild.value = value;
    },
  });

  // Exact px font-size — a curated preset dropdown complementing the legacy X-Small…XXX-Large sizes.
  // Pick a size and it is applied in pixels to the selection (or the whole text when nothing is
  // selected). The dropdown reflects the current size when it matches a preset.
  rte.add('fontSizePx', {
    icon: `<select class="gjs-rte-select">
      <option value="">Size (px)</option>
      ${FONT_SIZE_PX_PRESETS.map(
        (s) => `<option value="${s}">${s}px</option>`,
      ).join('')}
    </select>`,
    attributes: { title: 'Font size (px)' },
    event: 'change',
    result: (rte, action) => {
      const px = parseInt(action.btn.firstChild.value, 10);
      if (!px) return;
      applyFontSizePx(rte, px);
    },
    update: (rte, action) => {
      try {
        const doc = rte.doc;
        const win = doc.defaultView;
        const sel = doc.getSelection && doc.getSelection();
        let node = sel && sel.anchorNode;
        if (node && node.nodeType === 3) node = node.parentElement;
        if (node && win) {
          const size = parseInt(win.getComputedStyle(node).fontSize, 10);
          action.btn.firstChild.value = FONT_SIZE_PX_PRESETS.includes(size)
            ? String(size)
            : '';
          return;
        }
      } catch (e) {
        // fall through to clearing the field
      }
      action.btn.firstChild.value = '';
    },
  });

  // Line height — a preset dropdown. Applied to the SELECTED COMPONENT's style (not an inline RTE
  // span) so it exports as a CSS rule GrapesJS controls, and reads back the component's current value.
  rte.add('lineHeight', {
    icon: `<select class="gjs-rte-select">
      <option value="">Line height</option>
      ${LINE_HEIGHT_PRESETS.map(
        (v) => `<option value="${v}">${v}</option>`,
      ).join('')}
    </select>`,
    attributes: { title: 'Line height' },
    event: 'change',
    result: (rte, action) => {
      const value = action.btn.firstChild.value;
      const selected = editor.getSelected();
      if (value && selected) selected.addStyle({ 'line-height': value });
    },
    update: (rte, action) => {
      const selected = editor.getSelected();
      const current = selected && selected.getStyle()['line-height'];
      action.btn.firstChild.value = current || '';
    },
  });

  rte.add('bold', {
    icon: '<span class="gjs-rte-button icon icon-bold"></span>',
    attributes: { title: 'Bold' },
    result: (rte) => rte.exec('bold'),
  });

  rte.add('italic', {
    icon: '<span class="gjs-rte-button icon icon-italic"></span>',
    attributes: { title: 'Italic' },
    result: (rte) => rte.exec('italic'),
  });

  rte.add('strikethrough', {
    icon: '<span class="gjs-rte-button icon icon-strikethrough"></span>',
    attributes: { title: 'Strikethrough' },
    result: (rte) => rte.exec('strikethrough'),
  });

  rte.add('underline', {
    icon: '<span class="gjs-rte-button icon icon-format-underlined"></span>',
    attributes: { title: 'Underline' },
    result: (rte) => rte.exec('underline'),
  });

  // Text alignment — beginners expect these in the floating toolbar rather than the style panel
  // (which Email mode de-emphasises). execCommand justify* applies block-level alignment.
  [
    { name: 'justifyLeft', icon: 'icon-align-left', title: 'Align left' },
    { name: 'justifyCenter', icon: 'icon-align-center', title: 'Align center' },
    { name: 'justifyRight', icon: 'icon-align-right', title: 'Align right' },
    { name: 'justifyFull', icon: 'icon-align-justify', title: 'Justify' },
  ].forEach(({ name, icon, title }) => {
    rte.add(name, {
      icon: `<span class="gjs-rte-button icon ${icon}"></span>`,
      attributes: { title },
      result: (rte) => rte.exec(name),
    });
  });

  // Clear formatting — strips inline formatting (and links) from the selection, so a beginner can
  // escape the nested <font>/<span> soup that repeated size/colour/bold operations create.
  rte.add('clearFormat', {
    icon:
      '<span class="gjs-rte-button" title="Clear formatting">' +
      '<svg viewBox="0 0 24 24" width="16" height="16" style="vertical-align:middle">' +
      '<path fill="currentColor" d="M6 5v.18L8.82 8h2.4l-.72 1.68 2.1 2.1L14.21 8H20V5H6M3.27 5 2 6.27l6.97 6.97L6.5 19h3l1.57-3.66L16.73 21 18 19.73 3.55 5.27 3.27 5Z"/>' +
      '</svg></span>',
    attributes: { title: 'Clear formatting' },
    result: (rte) => {
      rte.exec('removeFormat');
      rte.exec('unlink');
    },
  });

  rte.add('foreColor', {
    icon: `<label class="gjs-rte-button gjs-rte-button--font-color icon icon-text-fields"><input class="gjs-rte-color" type="color" /></label>`,
    attributes: { title: 'Font color' },
    event: 'input',
    result: (rte, action) => {
      const { value } = action.btn.firstChild.firstChild;

      rte.exec('foreColor', value);
    },
  });

  rte.add('backColor', {
    icon: `<label class="gjs-rte-button gjs-rte-button--background-color icon icon-text-fields"><input class="gjs-rte-color" type="color" /></label>`,
    attributes: { title: 'Background color' },
    event: 'input',
    result: (rte, action) => {
      const { value } = action.btn.firstChild.firstChild;

      rte.exec('backColor', value);
    },
  });

  rte.add('link', {
    icon: '<span class="gjs-rte-button icon icon-link"></span>',
    attributes: { title: 'Link' },
    state: (rte) => {
      if (rte && rte.selection()) {
        return isValidAnchor(rte)
          ? RTE_BUTTON_STATE.ACTIVE
          : RTE_BUTTON_STATE.INACTIVE;
      } else {
        return RTE_BUTTON_STATE.INACTIVE;
      }
    },
    result: (rte) => {
      const blockType = editor.getSelected().get('type');

      if (blockType === 'button') {
        const attributes = editor.getSelected().get('attributes');

        const editURL = document.createElement('div');
        editURL.className = 'edit-url-modal__form';

        const editURLTitle = document.createElement('p');
        editURLTitle.className = 'edit-url-modal__form-title';
        editURLTitle.innerHTML = 'Add, edit or remove link';

        const editButtons = document.createElement('div');
        editButtons.className = 'edit-url-modal__buttons';

        const saveButton = document.createElement('button');
        saveButton.type = 'button';
        saveButton.className = 'dnd-button-base dnd-button contained default';
        saveButton.innerHTML = '<div class="dnd-button__label">Save</div>';
        saveButton.onclick = () => {
          const inputUrlValue = document.querySelector(
            '.edit-url-modal__url-input',
          ).value;
          const inputUrlTarget = document.querySelector(
            '.edit-url-modal__url-target',
          ).value;

          editor
            .getSelected()
            .addAttributes({ href: inputUrlValue, target: inputUrlTarget });
          editor.getSelected().view.render();
          editor.Modal.close();
        };

        const cancelButton = document.createElement('button');
        cancelButton.type = 'button';
        cancelButton.className = 'dnd-button-base dnd-button text default';
        cancelButton.innerHTML = '<div class="dnd-button__label">Cancel</div>';
        cancelButton.onclick = () => editor.Modal.close();

        const urlInput = document.createElement('input');
        const urlInputLable = document.createElement('label');
        urlInput.type = 'url';
        urlInput.placeholder = 'eg. https://google.com';
        urlInput.value = attributes.href || '';
        urlInput.className = 'edit-url-modal__url-input dnd-input__input';
        urlInput.id = 'edit-url-modal__url-input';
        urlInputLable.className = 'dnd-label';
        urlInputLable.setAttribute('for', 'edit-url-modal__url-input');
        urlInputLable.innerHTML = 'Link';

        const selectListWrapper = document.createElement('div');
        selectListWrapper.className = 'edit-url-modal__select-wrapper';
        const selectList = document.createElement('select');
        const thisWinOption = document.createElement('option');
        const newWinOption = document.createElement('option');
        const selectListLable = document.createElement('label');
        selectList.className = 'edit-url-modal__url-target';
        selectList.id = 'edit-url-modal__url-target';
        selectListLable.className = 'edit-url-modal__url-target';
        selectListLable.id = 'edit-url-modal__url-target';
        selectListLable.className = 'dnd-label';
        selectListLable.setAttribute('for', 'edit-url-modal__url-target');
        selectListLable.innerHTML = 'Target';
        thisWinOption.value = '_self';
        thisWinOption.text = 'This window';
        newWinOption.value = '_blank';
        newWinOption.text = 'New window';
        selectList.appendChild(thisWinOption);
        selectList.appendChild(newWinOption);

        editURL.appendChild(editURLTitle);
        editURL.appendChild(urlInputLable);
        editURL.appendChild(urlInput);
        editURL.appendChild(selectListLable);
        selectListWrapper.appendChild(selectList);
        editURL.appendChild(selectListWrapper);
        editButtons.appendChild(cancelButton);
        editButtons.appendChild(saveButton);
        editURL.appendChild(editButtons);

        if (attributes.target === '_blank') {
          newWinOption.setAttribute('selected', true);
        } else {
          thisWinOption.setAttribute('selected', true);
        }

        editor.Modal.setTitle('Link Settings')
          .setContent(editURL)
          .open({
            attributes: { class: 'edit-url-modal' },
          });
      } else {
        // Text selection: open the URL dialog so the link actually gets an href (add / edit /
        // remove), instead of inserting an un-editable empty-href anchor.
        openTextLinkModal(editor, rte);
      }
    },
  });
};

const addCustomFields = async (editor, dynamicFields) => {
  const rte = editor.RichTextEditor;

  rte.add('dynamicFields', {
    icon: `<select class="gjs-rte-select">
    <option value="">Custom fields</option>
    ${CUSTOM_FIELDS.reduce((acc, customField) => {
      acc += `<option value='${customField.value}'>${customField.name}</option>`;

      return acc;
    }, '')}
    ${dynamicFields.reduce((acc, customField) => {
      acc += `<option value='#{${customField.name}}#'>${customField.name}</option>`;

      return acc;
    }, '')}
  </select>`,
    attributes: { title: 'Custom fields' },
    event: 'change',
    result: (rte, action) => {
      const { value } = action.btn.firstChild;

      rte.insertHTML(value);
    },
    update: (rte, action) => {
      const select = action.btn.firstChild;
      const selection = rte.selection().toString();

      const isSelected = [...select.options].some(
        (option) => option.value === selection,
      );

      if (isSelected) {
        action.btn.firstChild.value = selection;
        return;
      }

      action.btn.firstChild.value = '';
    },
  });
};

export { richTextEditorConfig, richTextEditor, addCustomFields };
